import { Space } from "antd";
import Link from "next/link";
import { FormattedMessage } from "react-intl";

import type {
  TaxComponentShortFragment,
  TaxPlanWithComponentsFragment,
} from "../../../lib/graphql";
import routes from "../../../lib/routes";
import { HintTooltip } from "../../shared/HelpTooltip";

interface TaxComponentLabelProps {
  taxComponent: Pick<TaxComponentShortFragment, "id" | "name">;
  // Counterparty plans come without components; such plans are skipped
  taxPlans: Array<
    | Pick<TaxPlanWithComponentsFragment, "id" | "name">
    | TaxPlanWithComponentsFragment
    | null
    | undefined
  >;
}

export function TaxComponentLabel({
  taxComponent,
  taxPlans,
}: TaxComponentLabelProps) {
  const plans = Array.from(
    new Map(
      taxPlans
        .filter(
          (plan): plan is TaxPlanWithComponentsFragment =>
            !!plan &&
            "components" in plan &&
            plan.components.some(
              (component) => component.taxComponent.id === taxComponent.id
            )
        )
        .map((plan) => [plan.id, plan])
    ).values()
  );

  return (
    <span>
      {taxComponent.name}{" "}
      {plans.length > 0 && (
        <HintTooltip
          title={
            <Space direction="vertical" size="small">
              {plans.map((plan) => {
                const component = plan.components.find(
                  (candidate) => candidate.taxComponent.id === taxComponent.id
                )!;
                const priceApplication =
                  component.priceApplication ??
                  component.taxComponent.priceApplication;
                const inventoryImpact =
                  component.inventoryImpact ??
                  component.taxComponent.inventoryImpact;

                return (
                  <Space key={plan.id} direction="vertical" size={0}>
                    <span>
                      <FormattedMessage id="taxComponents.taxType" />:{" "}
                      <FormattedMessage
                        id={`taxComponents.taxTypes.${component.taxComponent.taxType}`}
                      />
                    </span>
                    <span>
                      <FormattedMessage id="taxComponents.priceApplication" />:{" "}
                      <FormattedMessage
                        id={`taxComponents.priceApplications.${priceApplication}`}
                      />
                    </span>
                    <span>
                      <FormattedMessage id="taxComponents.inventoryImpact" />:{" "}
                      <FormattedMessage
                        id={`taxComponents.inventoryImpacts.${inventoryImpact}`}
                      />
                    </span>
                    <span>
                      <FormattedMessage id="taxPlans.entityName" />:{" "}
                      <Link
                        href={routes.finance.taxPlans.details(plan.id)}
                        target="_blank"
                      >
                        {plan.name}
                      </Link>
                    </span>
                  </Space>
                );
              })}
            </Space>
          }
        />
      )}
    </span>
  );
}
